import type { LocatorJson } from "@msb/reader-bridge";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { ChatApi, type BookPerson, type ProductFeatures, type RemoteMessage } from "../chat/api.ts";
import {
  AroundMeError,
  aroundMeLabel,
  aroundMeQuestion,
  guideArea as toGuideArea,
  inArea,
  locate,
  outsideMessage,
  positionAtOpening,
  type GuideArea,
} from "../chat/aroundMe.ts";
import { bracketStageDirections, findCharacterConversation, personIcon, personsMet } from "../chat/characters.ts";
import { renderMarkdown } from "../chat/markdown.ts";
import { questionSuggestions } from "../chat/questions.ts";
import { linkQuotes, QUOTE_HREF, type QuoteSource } from "../chat/quoteLinks.ts";
import { detectLanguage } from "../chat/typography.ts";
import { parseExplainQuestion } from "../explain.ts";
import { recapAvailable, spoilerFreeSummaryQuestion } from "../recap.ts";
import { useChat } from "../chat/useChat.ts";
import { reactionDetail, reactionMessage, reactionOf } from "../chat/reactions.ts";
import { defineMessages, getLocale, translator, useLocale, useT, type Locale } from "../i18n/index.ts";
import { baseLanguage } from "../contentLanguage.ts";
import { Icon } from "../icons/Icon.tsx";
import { LanguagePicker, LanguageQuestion, useContentTexts, useDefaultLanguage } from "./ContentLanguage.tsx";
import { QuizPanel } from "./QuizPanel.tsx";
import { useReactionPicker } from "./ReactionPicker.tsx";

export interface ChatConfig {
  baseUrl: string;
  token: string;
  productId: string;
}

interface Props {
  title: string;
  config: ChatConfig;
  /** Feuille du bas (mobile) : poignée affichée. */
  sheet: boolean;
  onClose(): void;
  /** Un passage cité par une réponse : l'ouvrir dans le livre. */
  onQuote?(locator: LocatorJson): void;
  /** Question venue du livre (« Explique-moi ») : envoyée dès que le chat est prêt ; un nouveau `nonce` la renvoie. */
  ask?: { text: string; nonce: number };
  /**
   * Où en est le lecteur, pour le récap sans spoiler et le quiz : rang du chapitre (0 : inconnu), nom de la section
   * en cours, fichier lu et fichiers du livre dans l'ordre.
   */
  reading?: { chapter: number; section?: string; href?: string; readingOrder?: string[] };
  /** Le récap est permis par l'application et par le back-office pour ce livre. */
  recap?: boolean;
  /** Réglages du back-office pour ce livre (catégorie, questions, chapitre du récap) ; `undefined` tant qu'on ne sait pas. */
  book?: ProductFeatures | null;
  /** « Discuter avec… » une personne du livre est permis (application et back-office). */
  talkWith?: boolean;
  /** Le lecteur est-il arrivé à ce fichier du livre ? Les personnes qui apparaissent plus loin ne sont pas proposées. */
  reached?(href: string): boolean;
  /** Le quiz est permis (application et back-office). */
  quiz?: boolean;
  /** « Autour de moi » est permis : un guide de voyage (application et back-office). */
  aroundMe?: boolean;
  /** La couverture du livre, en haut de la conversation et au milieu, comme dans l'appli ChatNovel. */
  cover?: string;
}

const messages = defineMessages(
  {
    panel: "Discussion avec le livre",
    close: "Fermer la discussion",
    title: "Discussion",
    emptyTitle: "Ouvrons la conversation.",
    emptyText: "Posez une question sur « {title} » : une idée, un personnage, un résumé…",
    coverOf: "Couverture de « {title} »",
    rate: "Noter cette réponse",
    helpful: "Réponse utile",
    notHelpful: "Réponse insatisfaisante",
    thanks: "Merci pour votre avis !",
    whatWentWrong: "Qu’est-ce qui n’allait pas avec cette réponse ?",
    describeProblem: "Décrivez le problème avec cette réponse…",
    sendComment: "Envoyer",
    sending: "Envoi…",
    cancel: "Annuler",
    yourComment: "Votre commentaire :",
    tellMore: "{reaction} : voulez-vous en dire plus ?",
    optionalDetail: "Facultatif : ce qui n’allait pas…",
    rateFailed: "Votre avis n’a pas pu être enregistré. Réessayez.",
    searching: "Recherche dans le livre…",
    connecting: "Connexion au chatbot…",
    reconnecting: "Connexion perdue, reconnexion…",
    placeholder: "Posez-moi une question",
    question: "Votre question",
    answering: "Réponse en cours",
    send: "Envoyer",
    expired: "Votre session de lecture a expiré. Rouvrez le livre depuis votre bibliothèque.",
    unavailable: "Le chatbot n'est pas disponible pour ce livre.",
    unreachable: "Impossible de joindre le chatbot.",
    busy: "Une réponse est déjà en cours, patientez un instant.",
    failed: "La question n'a pas pu être traitée. Réessayez.",
    seeInBook: "Voir dans le livre",
    recap: "Fais-moi le récap de tout ce qui précède",
    recapShort: "Récap",
    backToBook: "Revenir à la discussion du livre",
    talkWith: "Discuter avec {name}",
    talkWithEllipsis: "Discuter avec…",
    quiz: "Quiz",
    locating: "Localisation en cours…",
    locationFailed: "Votre position n’a pas pu être trouvée.",
    bookPeople: "Personnes du livre",
    peopleMetSoFar: "Personnages apparus à ce stade du livre :",
    noPersonMetYet: "Au point où vous en êtes de votre lecture, vous n’avez pas encore croisé de personnages. Avancez un peu !",
    talkWithHeading: "Discutez avec {name}.",
    talkWithIntro: "{name} répond d’après « {title} », sans en savoir plus que ce que le livre permet de savoir.",
    characterQuestion1: "Qui es-tu ?",
    characterQuestion2: "Raconte-moi ton histoire.",
    characterQuestion3: "Que penses-tu de ce qui t’arrive ?",
    characterQuestion4: "Qu’est-ce qui compte le plus pour toi ?",
    confirmWhere: "Confirmez-vous que vous en êtes là ?",
    currentlyAt: "En cours : {section}",
    summarizeUpToHere: "Oui, résumer jusqu’ici",
    noCancel: "Non, annuler",
  },
  {
    panel: "Chat with the book",
    close: "Close the chat",
    title: "Chat",
    emptyTitle: "Let's start the conversation.",
    emptyText: "Ask a question about “{title}”: an idea, a character, a summary…",
    coverOf: "Cover of “{title}”",
    rate: "Rate this answer",
    helpful: "Helpful answer",
    notHelpful: "Unsatisfying answer",
    thanks: "Thanks for your feedback!",
    whatWentWrong: "What was wrong with this answer?",
    describeProblem: "Describe the problem with this answer…",
    sendComment: "Send",
    sending: "Sending…",
    cancel: "Cancel",
    yourComment: "Your comment:",
    tellMore: "{reaction}: anything to add?",
    optionalDetail: "Optional: what was wrong…",
    rateFailed: "Your feedback couldn't be saved. Please try again.",
    searching: "Searching the book…",
    connecting: "Connecting to the chatbot…",
    reconnecting: "Connection lost, reconnecting…",
    placeholder: "Ask me a question",
    question: "Your question",
    answering: "Answer in progress",
    send: "Send",
    expired: "Your reading session has expired. Reopen the book from your library.",
    unavailable: "The chatbot isn't available for this book.",
    unreachable: "Couldn't reach the chatbot.",
    busy: "An answer is already on its way, please wait a moment.",
    failed: "Your question couldn't be processed. Please try again.",
    seeInBook: "See in the book",
    recap: "Give me the recap of everything so far",
    recapShort: "Recap",
    backToBook: "Back to the chat with the book",
    talkWith: "Chat with {name}",
    talkWithEllipsis: "Chat with…",
    quiz: "Quiz",
    locating: "Finding your location…",
    locationFailed: "Your location could not be found.",
    bookPeople: "People in the book",
    peopleMetSoFar: "Characters met so far in the book:",
    noPersonMetYet: "You haven’t met any characters yet at this point in your reading. Read on a little!",
    talkWithHeading: "Chat with {name}.",
    talkWithIntro: "{name} answers based on “{title}”, knowing no more than the book lets them know.",
    characterQuestion1: "Who are you?",
    characterQuestion2: "Tell me your story.",
    characterQuestion3: "What do you make of what’s happening to you?",
    characterQuestion4: "What matters most to you?",
    confirmWhere: "Is this where you are?",
    currentlyAt: "Currently reading: {section}",
    summarizeUpToHere: "Yes, summarize up to here",
    noCancel: "No, cancel",
  },
);

const characterQuestions = ["characterQuestion1", "characterQuestion2", "characterQuestion3", "characterQuestion4"] as const;

type Rate = (messageId: string, isGood: boolean | null, comment?: string | null) => Promise<boolean>;

/**
 * Pouces sous une réponse, comme Studio (`MessageRating`) : le pouce levé est enregistré tout de suite ; le pouce
 * baissé ouvre une zone pour dire ce qui n'allait pas, envoyée avec la note (`rating_message`).
 */
function Rating({ message, rate }: { message: RemoteMessage; rate: Rate }) {
  const t = useT(messages);
  const [commenting, setCommenting] = useState(false);
  // Avec une réaction, la zone ne contient que le commentaire écrit après elle.
  const reaction = reactionOf(message.rating, message.rating_message);
  const negativeReaction = message.rating === 0 ? reaction : null;
  const commentOf = () => (reaction ? reactionDetail(reaction, message.rating_message) : (message.rating_message ?? ""));
  const [comment, setComment] = useState(commentOf);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const box = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (commenting) {
      box.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
      box.current?.querySelector("textarea")?.focus();
    }
  }, [commenting]);

  const save = async (isGood: boolean | null, text: string | null = null) => {
    setSaving(true);
    setFailed(false);
    const saved = await rate(message.id, isGood, text);
    setSaving(false);
    setFailed(!saved);
    if (saved) setCommenting(false);
    return saved;
  };
  const submit = (e: Event) => {
    e.preventDefault();
    if (negativeReaction) void save(false, reactionMessage(negativeReaction, comment));
    else if (comment.trim()) void save(false, comment.trim());
  };
  const cancel = () => {
    setCommenting(false);
    setFailed(false);
    setComment(commentOf());
  };
  // Appui long ou survol d'un pouce : emojis pour préciser l'avis, comme dans l'app.
  // Un emoji de pouce baissé est enregistré tout de suite, puis la zone s'ouvre pour en dire plus.
  const reactions = useReactionPicker(message.rating, message.rating_message, async (isGood, text) => {
    setCommenting(false);
    const saved = await save(isGood, text);
    if (saved && isGood === false) {
      setComment("");
      setCommenting(true);
    }
  });
  const reacted = (rating: number) =>
    message.rating === rating && reactions.reaction ? (
      <span class="msb-rating__emoji" aria-hidden="true">
        {reactions.reaction.emoji}
      </span>
    ) : null;

  return (
    <div class="msb-rating-block">
      <div class="msb-rating" role="group" aria-label={t("rate")} {...reactions.groupProps}>
        <button
          type="button"
          class="msb-icon-button"
          aria-label={t("helpful")}
          aria-pressed={message.rating === 1}
          disabled={saving}
          {...reactions.triggers("up")}
          onClick={() => {
            if (reactions.endedLongPress()) return;
            setCommenting(false);
            void save(message.rating === 1 ? null : true);
          }}
        >
          {reacted(1) ?? <Icon name={message.rating === 1 ? "thumbUpFilled" : "thumbUp"} />}
        </button>
        <button
          type="button"
          class="msb-icon-button"
          aria-label={t("notHelpful")}
          aria-pressed={message.rating === 0 || commenting}
          disabled={saving}
          {...reactions.triggers("down")}
          onClick={() => {
            if (reactions.endedLongPress()) return;
            if (message.rating === 0) void save(null);
            else setCommenting((open) => !open);
          }}
        >
          {reacted(0) ?? <Icon name={message.rating === 0 || commenting ? "thumbDownFilled" : "thumbDown"} />}
        </button>
        {message.rating !== null && !commenting && <span class="msb-rating__thanks">{t("thanks")}</span>}
        {reactions.menu}
      </div>
      {commenting && (
        <form ref={box} class="msb-rating__comment" onSubmit={submit}>
          <label for={`comment-${message.id}`}>
            {negativeReaction
              ? t("tellMore", { reaction: `${negativeReaction.emoji} ${getLocale() === "en" ? negativeReaction.en : negativeReaction.fr}` })
              : t("whatWentWrong")}
          </label>
          <textarea
            id={`comment-${message.id}`}
            rows={3}
            value={comment}
            placeholder={t(negativeReaction ? "optionalDetail" : "describeProblem")}
            disabled={saving}
            onInput={(e) => setComment(e.currentTarget.value)}
          />
          <div class="msb-rating__actions">
            <button type="submit" class="msb-rating__send" disabled={saving || (!negativeReaction && !comment.trim())}>
              {t(saving ? "sending" : "sendComment")}
            </button>
            <button type="button" class="msb-rating__cancel" disabled={saving} onClick={cancel}>
              {t("cancel")}
            </button>
          </div>
        </form>
      )}
      {failed && (
        <p class="msb-rating__error" role="alert">
          {t("rateFailed")}
        </p>
      )}
      {message.rating === 0 && !commenting && message.rating_message && (!reaction || commentOf()) && (
        <div class="msb-rating__saved">
          <p class="msb-rating__saved-label">{t("yourComment")}</p>
          <p>{commentOf()}</p>
        </div>
      )}
    </div>
  );
}

interface MarkdownProps {
  content: string;
  /** Avec `onQuote`, les textes cités de la réponse deviennent des liens vers le livre. */
  quotes?: QuoteSource[] | null;
  onQuote?(locator: LocatorJson): void;
  quoteTitle?: string;
}

/** Une réponse : Markdown, typographie de sa langue, et passages cités reliés au livre (comme Studio). */
function Markdown({ content, quotes, onQuote, quoteTitle }: MarkdownProps) {
  const linked = useMemo(() => (onQuote ? linkQuotes(content, quotes) : { markdown: content, targets: [] }), [content, quotes, onQuote]);
  const language = useMemo(() => detectLanguage(content), [content]);
  const html = useMemo(() => renderMarkdown(linked.markdown, language), [linked, language]);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    for (const link of box.current?.querySelectorAll<HTMLAnchorElement>('a[href^="#quote-"]') ?? []) {
      link.classList.add("msb-quote-link");
      if (quoteTitle) link.title = quoteTitle;
    }
  }, [html, quoteTitle]);
  const onClick = (event: MouseEvent) => {
    const link = (event.target as Element | null)?.closest?.("a");
    const match = link ? QUOTE_HREF.exec(link.getAttribute("href") ?? "") : null;
    if (!match) return;
    event.preventDefault();
    const target = linked.targets[Number(match[1])];
    if (target) onQuote?.(target);
  };
  return <div ref={box} class="msb-prose" lang={language} onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Une question du lecteur ; « Explique ce passage » montre la question puis le passage cité. */
function UserMessage({ content, pending = false }: { content: string; pending?: boolean }) {
  const explain = parseExplainQuestion(content);
  return (
    <article class={pending ? "msb-message msb-message--user msb-message--pending" : "msb-message msb-message--user"}>
      {explain ? (
        <>
          <p>
            <em>{explain.question}</em>
          </p>
          <blockquote class="msb-message__passage">{explain.passage}</blockquote>
        </>
      ) : (
        <p>{content}</p>
      )}
    </article>
  );
}

/** Discussion avec le livre, dans l'esprit du chat de studio.mysmartbook.fr et de l'app. */
export function ChatPanel({ title, config, sheet, onClose, onQuote, ask, reading, recap = false, book, talkWith = false, reached, quiz = false, aroundMe = false, cover }: Props) {
  const t = useT(messages);
  const locale = useLocale();
  const api = useMemo(() => new ChatApi({ baseUrl: config.baseUrl, token: config.token }), [config.baseUrl, config.token]);
  // « Discuter avec… » (repris de Studio) : un dialogue à part avec une personne du livre, repris s'il existe déjà.
  const [talkingWith, setTalkingWith] = useState<BookPerson | null>(null);
  const [persons, setPersons] = useState<BookPerson[]>();
  const [peopleOpen, setPeopleOpen] = useState(false);
  useEffect(() => {
    if (!talkWith) {
      setPersons(undefined);
      setTalkingWith(null);
      return;
    }
    let cancelled = false;
    api.getCharacters(config.productId).then(
      (found) => !cancelled && setPersons(found),
      () => !cancelled && setPersons([]),
    );
    return () => {
      cancelled = true;
    };
  }, [api, config.productId, talkWith]);
  const met = useMemo(
    () => personsMet(persons ?? [], reached ?? (() => true), book?.settings.max_persons),
    [persons, reached, book],
  );
  const openCharacter = (person: BookPerson) => async (chat: ChatApi) =>
    findCharacterConversation(await chat.listConversations(), config.productId, person.name) ??
    chat.createCharacterConversation(config.productId, person);
  const { state, status, readyKey, send: sendInChat, rate, language: conversationLanguage } = useChat({
    api,
    productId: config.productId,
    key: talkingWith ? `character:${talkingWith.name}` : "book",
    open: talkingWith ? openCharacter(talkingWith) : undefined,
  });
  const talkTo = (person: BookPerson) => {
    setPeopleOpen(false);
    setTalkingWith(person);
  };
  // Le quiz (repris de Studio) prend la place de la discussion, jusqu'au retour.
  const [quizOpen, setQuizOpen] = useState(false);
  useEffect(() => {
    if (!quiz) setQuizOpen(false);
  }, [quiz]);
  const quizPosition = useMemo(
    () => (reading?.href ? { href: reading.href, readingOrder: reading.readingOrder ?? [], section: reading.section } : undefined),
    [reading],
  );
  const [draft, setDraft] = useState("");
  const end = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [state.messages.length, state.partialAnswer, state.pendingQuestion, state.processing]);

  // La zone de saisie grandit avec la question, jusqu'à une limite.
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);

  // La langue de la conversation : les drapeaux de sa question, sur celle choisie pour le livre tant qu'on n'en change
  // pas ; ils restent, pour en changer à toute question (Stéphane, 19/10/2026). Le serveur la garde à chaque question.
  const defaultLanguage = useDefaultLanguage();
  const [draftLanguage, setDraftLanguage] = useState<string | null>(null);
  useEffect(() => setDraftLanguage(null), [talkingWith]);
  const started = !!state.pendingQuestion || state.messages.some((m) => m.role === "user");
  const chatLanguage = draftLanguage ?? conversationLanguage ?? defaultLanguage;
  const send = (question: string) => sendInChat(question, chatLanguage);
  // Les réponses dans la langue de la conversation (la réponse en cours arrive à part).
  const shownAnswers = useContentTexts(
    state.messages.map((m) => (m.role === "assistant" ? m.content : "")),
    chatLanguage,
  );

  const fatal = status.kind === "error" && status.fatal;
  const busy = state.streaming;

  // Une question venue du livre part une fois, dans la conversation du livre, dès qu'elle est prête et libre.
  const asked = useRef<number>();
  useEffect(() => {
    if (ask && asked.current !== ask.nonce) {
      setTalkingWith(null);
      setQuizOpen(false);
    }
  }, [ask]);
  useEffect(() => {
    if (!ask || asked.current === ask.nonce || status.kind !== "ready" || readyKey !== "book" || busy) return;
    asked.current = ask.nonce;
    void send(ask.text);
  }, [ask, status.kind, readyKey, busy]);
  const canSend = status.kind !== "connecting" && !fatal && !busy && draft.trim().length > 0;
  const empty = state.messages.length === 0 && !state.pendingQuestion && status.kind === "ready";
  // Les questions de départ dans la langue choisie dans la zone de question (Stéphane, 19/10/2026) : écrites dans
  // celle-ci si c'est une langue de l'interface, traduites sinon (telles quelles le temps que la traduction arrive).
  const chosenLanguage = baseLanguage(chatLanguage);
  const interfaceLanguage = chosenLanguage === "fr" || chosenLanguage === "en";
  const questionLocale: Locale = interfaceLanguage ? (chosenLanguage as Locale) : locale;
  // Questions de départ selon la catégorie du livre, ou celles du back-office ; attendues pour ne pas changer sous les yeux.
  const openingQuestions = useMemo(
    () => {
      const tq = translator(messages, questionLocale);
      return [
        tq("recap"),
        ...(talkingWith
          ? characterQuestions.map((key) => tq(key))
          : book === undefined
            ? []
            : questionSuggestions(book?.category, questionLocale, book?.settings.questions)),
      ];
    },
    [book, questionLocale, talkingWith],
  );
  const [recapLabel, ...suggestions] = useContentTexts(
    openingQuestions,
    // Montrées, donc traduites, seulement dans une conversation pas encore commencée.
    started || interfaceLanguage ? null : chosenLanguage || null,
  );

  // « Récap » (repris de Studio) : proposé à partir du 2ᵉ chapitre si le back-office le permet, vérifié d'abord
  // dans une carte au-dessus du champ : le lecteur confirme où il en est, la réponse s'arrête avant ce chapitre.
  const [summaryAsk, setSummaryAsk] = useState(false);
  const canRecap = !talkingWith && recapAvailable(recap, reading?.chapter ?? 0, book?.settings.summary_min_chapter);
  const canTalk = talkWith && !talkingWith && (persons?.length ?? 0) > 0;
  const section = reading?.section?.trim();
  const askSummary = () => {
    setSummaryAsk(false);
    void send(spoilerFreeSummaryQuestion(section, questionLocale));
  };
  useEffect(() => {
    if (!canRecap) setSummaryAsk(false);
  }, [canRecap]);

  const canQuiz = quiz && !talkingWith;

  // « Autour de moi » (repris de Studio) : la zone du guide et la position cherchée à l'ouverture grisent le bouton
  // loin du guide ; sinon la zone est vérifiée quand il sert, puis la question part dans la conversation du livre.
  const [area, setArea] = useState<GuideArea | null>();
  const [openingPosition, setOpeningPosition] = useState<{ latitude: number; longitude: number }>();
  useEffect(() => {
    setArea(undefined);
    if (!aroundMe) return;
    let cancelled = false;
    api.getGuideArea(config.productId).then(
      (data) => !cancelled && setArea(toGuideArea(data)),
      () => !cancelled && setArea(null),
    );
    void positionAtOpening().then((found) => !cancelled && found && setOpeningPosition(found));
    return () => {
      cancelled = true;
    };
  }, [api, config.productId, aroundMe]);
  const outside = !!area && !!openingPosition && !inArea(area, openingPosition.latitude, openingPosition.longitude);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const canAroundMe = aroundMe && !talkingWith;
  const askAroundMe = async () => {
    if (!canAroundMe || locating) return;
    if (outside && area) {
      setLocationError(outsideMessage(area, locale));
      return;
    }
    setLocationError(null);
    setLocating(true);
    try {
      const place = await locate(locale);
      if (area && !inArea(area, place.latitude, place.longitude)) {
        setLocationError(outsideMessage(area, locale));
        return;
      }
      void send(aroundMeQuestion(place, locale));
    } catch (error) {
      setLocationError(error instanceof AroundMeError ? error.message : t("locationFailed"));
    } finally {
      setLocating(false);
    }
  };
  const aroundMeButton = (className: string, size: number) => (
    <button
      type="button"
      class={className}
      disabled={locating || busy}
      aria-disabled={outside || undefined}
      title={outside && area ? outsideMessage(area, locale) : undefined}
      onClick={() => void askAroundMe()}
    >
      <Icon name="nearMe" size={size} />
      {aroundMeLabel(locale)}
    </button>
  );

  const submit = (e?: Event) => {
    e?.preventDefault();
    if (!canSend) return;
    void send(draft);
    setDraft("");
  };

  if (quizOpen) {
    return (
      <QuizPanel
        api={api}
        productId={config.productId}
        position={quizPosition}
        sheet={sheet}
        onBack={() => setQuizOpen(false)}
        onClose={onClose}
      />
    );
  }

  return (
    <section class="msb-chat" aria-label={t("panel")}>
      {sheet && <div class="msb-sheet-handle" aria-hidden="true" />}
      <header class="msb-chat__header">
        <button type="button" class="msb-icon-button" onClick={onClose} aria-label={t("close")}>
          <Icon name="close" />
        </button>
        {talkingWith ? (
          <h2 class="msb-chat__title--person">
            <Icon name={personIcon(talkingWith)} size={20} />
            <span>{talkingWith.name}</span>
          </h2>
        ) : (
          <h2>{t("title")}</h2>
        )}
        <div class="msb-chat__end">
          {talkingWith ? (
            <button type="button" class="msb-icon-button" onClick={() => setTalkingWith(null)} aria-label={t("backToBook")} title={t("backToBook")}>
              <Icon name="back" />
            </button>
          ) : (
            <span class="msb-chat__spacer" />
          )}
        </div>
      </header>

      <div class="msb-chat__messages" aria-live="polite" aria-busy={busy}>
        {/* Demandé au premier chat ou quiz sur un livre dans une autre langue (Stéphane, 19/10/2026). */}
        {!talkingWith && <LanguageQuestion />}
        {cover && !talkingWith && (
          <div class="msb-chat__cover">
            <img src={cover} alt={t("coverOf", { title })} />
          </div>
        )}
        {empty && (
          <div class="msb-chat__empty">
            {!(cover && !talkingWith) && <Icon name="chat" size={40} />}
            {talkingWith ? (
              <>
                <h3>{t("talkWithHeading", { name: talkingWith.name })}</h3>
                <p>{t("talkWithIntro", { name: talkingWith.name, title })}</p>
              </>
            ) : (
              <>
                <h3>{t("emptyTitle")}</h3>
                <p>{t("emptyText", { title })}</p>
              </>
            )}
            {!summaryAsk && (
              <div class="msb-chat__suggestions">
                {canRecap && (
                  <button type="button" onClick={() => setSummaryAsk(true)}>
                    {recapLabel}
                  </button>
                )}
                {canAroundMe && aroundMeButton("msb-chat__around", 18)}
                {suggestions.map((question) => (
                  <button type="button" key={question} disabled={busy} onClick={() => void send(question)}>
                    {question}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {state.messages.map((m, index) =>
          m.role === "user" ? (
            <UserMessage key={m.id} content={m.content} />
          ) : (
            <article key={m.id} class="msb-message msb-message--assistant">
              <Markdown
                content={talkingWith ? bracketStageDirections(shownAnswers[index] ?? m.content) : (shownAnswers[index] ?? m.content)}
                quotes={m.metadata?.quotes}
                onQuote={onQuote}
                quoteTitle={t("seeInBook")}
              />
              <Rating message={m} rate={rate} />
            </article>
          ),
        )}

        {state.pendingQuestion && (
          <UserMessage content={state.pendingQuestion} pending />
        )}
        {busy &&
          (state.partialAnswer ? (
            <article class="msb-message msb-message--assistant">
              <Markdown content={talkingWith ? bracketStageDirections(state.partialAnswer) : state.partialAnswer} />
            </article>
          ) : (
            <p class="msb-chat__thinking" role="status">
              <span class="msb-dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              {state.processing ?? t("searching")}
            </p>
          ))}
        {locating && (
          <p class="msb-chat__thinking" role="status">
            <span class="msb-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            {t("locating")}
          </p>
        )}
        {locationError && (
          <p class="msb-chat__location-error" role="alert">
            {locationError}
          </p>
        )}
        <div ref={end} />
      </div>

      {status.kind === "connecting" && <p class="msb-chat__status">{t("connecting")}</p>}
      {status.kind === "error" && (
        <p class="msb-chat__status msb-chat__status--error" role="alert">
          {t(status.reason)}
        </p>
      )}
      {status.kind === "ready" && !state.online && <p class="msb-chat__status">{t("reconnecting")}</p>}

      {summaryAsk && (
        <div class="msb-summary-ask" role="group" aria-label={t("recapShort")}>
          <p class="msb-summary-ask__question">{t("confirmWhere")}</p>
          {section && <p class="msb-summary-ask__where">{t("currentlyAt", { section })}</p>}
          <button type="button" class="msb-summary-ask__choice" data-main="true" disabled={busy} onClick={askSummary}>
            {t("summarizeUpToHere")}
          </button>
          <button type="button" class="msb-summary-ask__choice" onClick={() => setSummaryAsk(false)}>
            {t("noCancel")}
          </button>
        </div>
      )}
      {peopleOpen && canTalk && (
        <div class="msb-summary-ask msb-people" role="group" aria-label={t("bookPeople")}>
          {met.length === 0 ? (
            <p class="msb-summary-ask__where" role="status">
              {t("noPersonMetYet")}
            </p>
          ) : (
            <>
              <p class="msb-summary-ask__question">{t("peopleMetSoFar")}</p>
              {met.map((person) => (
                <button
                  type="button"
                  key={person.name}
                  class="msb-summary-ask__choice msb-people__person"
                  title={person.description ?? undefined}
                  onClick={() => talkTo(person)}
                >
                  <Icon name={personIcon(person)} size={18} />
                  <span>{person.name}</span>
                </button>
              ))}
            </>
          )}
          <button type="button" class="msb-summary-ask__choice" onClick={() => setPeopleOpen(false)}>
            {t("cancel")}
          </button>
        </div>
      )}
      {/* Raccourcis au-dessus du champ (le menu « + » de Studio) : le récap une fois la conversation commencée, et
          « Discuter avec… », le quiz. */}
      {((canRecap && !empty) || canTalk || canQuiz || (canAroundMe && !empty)) && !summaryAsk && !peopleOpen && !busy && status.kind === "ready" && (
        <div class="msb-chat__shortcuts">
          {canRecap && !empty && (
            <button type="button" class="msb-chat__shortcut" onClick={() => setSummaryAsk(true)}>
              <Icon name="skipPrevious" size={18} />
              {t("recapShort")}
            </button>
          )}
          {canTalk && (
            <button
              type="button"
              class="msb-chat__shortcut"
              onClick={() => (met.length === 1 ? talkTo(met[0]!) : setPeopleOpen(true))}
            >
              <Icon name="person" size={18} />
              {met.length === 1 ? t("talkWith", { name: met[0]!.name }) : t("talkWithEllipsis")}
            </button>
          )}
          {canAroundMe && !empty && aroundMeButton("msb-chat__shortcut", 18)}
          {canQuiz && (
            <button type="button" class="msb-chat__shortcut" onClick={() => setQuizOpen(true)}>
              <Icon name="help" size={18} />
              {t("quiz")}
            </button>
          )}
        </div>
      )}

      <form class="msb-composer" onSubmit={submit}>
        <textarea
          ref={input}
          rows={1}
          value={draft}
          placeholder={t("placeholder")}
          aria-label={t("question")}
          disabled={fatal || status.kind === "connecting"}
          onInput={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.isComposing) submit(e);
          }}
        />
        {/* La langue de la conversation, à changer à toute question (Stéphane, 19/10/2026). */}
        {chatLanguage && (
          <LanguagePicker value={chatLanguage} onChange={setDraftLanguage} disabled={busy} />
        )}
        <button type="submit" class="msb-send" disabled={!canSend} aria-label={t(busy ? "answering" : "send")}>
          <Icon name="send" />
        </button>
      </form>
    </section>
  );
}
